import { useLocation, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import ViewColumnOutlined from '@mui/icons-material/ViewColumnOutlined';
import TextureOutlined from '@mui/icons-material/TextureOutlined';
import PaletteOutlined from '@mui/icons-material/PaletteOutlined';
import PeopleOutlined from '@mui/icons-material/PeopleOutlined';
import WorkspacePremiumOutlined from '@mui/icons-material/WorkspacePremiumOutlined';
import FactoryOutlined from '@mui/icons-material/FactoryOutlined';
import AttachMoneyOutlined from '@mui/icons-material/AttachMoneyOutlined';
import PublicOutlined from '@mui/icons-material/PublicOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import CalculateOutlined from '@mui/icons-material/CalculateOutlined';
import GradientOutlined from '@mui/icons-material/GradientOutlined';
import PriceChangeOutlined from '@mui/icons-material/PriceChangeOutlined';
import DiamondOutlined from '@mui/icons-material/DiamondOutlined';
import StarOutlined from '@mui/icons-material/StarOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import RoomPreferencesOutlined from '@mui/icons-material/RoomPreferencesOutlined';
import ViewInArOutlined from '@mui/icons-material/ViewInArOutlined';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import BoltOutlined from '@mui/icons-material/BoltOutlined';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 260;

// `core: true`  → always visible to everyone (essentials).
// `section: 'x'` → visible to non-admins only if that permission is granted.
// no core/section → admin-only (hidden for non-admins).
const navItems = [
  { label: 'Dashboard', icon: DashboardOutlined, path: '/dashboard', core: true },
  { label: 'Products', icon: Inventory2Outlined, path: '/products', core: true },
  { label: 'Fields', icon: ViewColumnOutlined, path: '/fields' },
  { label: 'Fabrics', icon: TextureOutlined, path: '/fabrics', section: 'fabrics' },
  { label: 'Fabric Grades', icon: GradientOutlined, path: '/fabric-grades' },
  { label: 'Grade Pricing', icon: PriceChangeOutlined, path: '/fabric-grade-pricing' },
  { label: 'Finishes', icon: PaletteOutlined, path: '/finishes', section: 'finishes' },
  { label: 'Certifications', icon: WorkspacePremiumOutlined, path: '/certifications', section: 'certifications' },
  { label: 'Manufacturers', icon: FactoryOutlined, path: '/manufacturers' },
  { label: 'Currencies', icon: AttachMoneyOutlined, path: '/currencies' },
  { label: 'Countries', icon: PublicOutlined, path: '/countries' },
  { label: 'Fabric Coatings', icon: LayersOutlined, path: '/fabric-coatings', section: 'fabric_coatings' },
  { label: 'Installations', icon: PhotoCameraOutlined, path: '/installations', section: 'installations' },
  { label: 'Materials', icon: CategoryOutlined, path: '/materials', section: 'materials' },
  { label: 'Features', icon: StarOutlined, path: '/features', section: 'features' },
  { label: 'Options', icon: TuneOutlined, path: '/options' },
  { label: 'Settings', icon: RoomPreferencesOutlined, path: '/settings', section: 'settings' },
  { label: 'Calculations', icon: CalculateOutlined, path: '/calculations' },
  { label: 'Bulk Operations', icon: BoltOutlined, path: '/bulk-operations' },
  { label: 'Configurator', icon: ViewInArOutlined, path: '/configurator' },
];

const Sidebar = () => {
  const location = useLocation();
  const { user, canAccessSection } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Admins see everything. Non-admins see ONLY the essentials (core) plus the
  // permission sections that are granted — every other page is hidden.
  const visibleNav = isAdmin
    ? navItems
    : navItems.filter((item) => item.core || (item.section && canAccessSection(item.section)));

  const allNavItems = isAdmin
    ? [...visibleNav,
        { label: 'Logs', icon: HistoryOutlined, path: '/logs' },
        { label: 'Users', icon: PeopleOutlined, path: '/users' },
      ]
    : visibleNav;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <DiamondOutlined sx={{ color: 'primary.main', fontSize: 28 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
          Prince Seating
        </Typography>
      </Box>

      <List sx={{ px: 1.5, pt: 1 }}>
        {allNavItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

          return (
            <ListItemButton
              key={item.path}
              component={RouterLink}
              to={item.path}
              selected={isActive}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                '&.Mui-selected': {
                  bgcolor: 'rgba(108, 99, 255, 0.08)',
                  color: 'primary.main',
                  '& .MuiListItemIcon-root': { color: 'primary.main' },
                  '&:hover': { bgcolor: 'rgba(108, 99, 255, 0.12)' },
                },
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                <item.icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive ? 600 : 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
};

export { DRAWER_WIDTH };
export default Sidebar;
